import axios from 'axios';
import { getUserData } from './storageService';

const API_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/revenue`
  : 'https://backend-c-0mxm.onrender.com/api/revenue';

const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use((config) => {
  const user = getUserData();
  if (user) {
    config.headers.Authorization = `Bearer ${user.userId || user.token}`;
  }
  return config;
});

const handleError = (error) => {
  if (error.response) {
    return { message: error.response.data.message || 'Có lỗi xảy ra', status: error.response.status };
  }
  if (error.request) {
    return { message: 'Không thể kết nối đến server', status: 0 };
  }
  return { message: 'Có lỗi xảy ra khi tạo request', status: 0 };
};

/**
 * User tạo revenue claim kèm ảnh bằng chứng
 * @param {FormData} formData - chứa livestreamId, totalSales, platform, sessionLink, sessionCode, notes, evidence (files)
 */
export const createRevenue = async (formData) => {
  try {
    const response = await api.post('/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

/**
 * User lấy danh sách revenue claims của mình
 */
export const getMyRevenues = async (params = {}) => {
  try {
    const response = await api.get('/my', { params });
    return { success: true, data: response.data.data, pagination: response.data.pagination };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

/**
 * Lấy revenue theo phiên live
 */
export const getRevenueBySession = async (livestreamId) => {
  try {
    const response = await api.get(`/session/${livestreamId}`);
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

/**
 * User sửa claim (chỉ khi draft hoặc rejected)
 */
export const updateRevenue = async (id, formData) => {
  try {
    const response = await api.put(`/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

/**
 * Upload thêm ảnh bằng chứng
 */
export const uploadEvidence = async (id, formData) => {
  try {
    const response = await api.post(`/${id}/evidence`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

/**
 * Xóa ảnh bằng chứng
 */
export const deleteEvidence = async (revenueId, evidenceId) => {
  try {
    const response = await api.delete(`/${revenueId}/evidence/${evidenceId}`);
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

// ============================================================
// ADMIN
// ============================================================

/**
 * Admin lấy tất cả revenue claims
 */
export const adminGetAllRevenues = async (params = {}) => {
  try {
    const response = await api.get('/admin/all', { params });
    return { success: true, data: response.data.data, pagination: response.data.pagination };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

/**
 * Admin tổng hợp doanh thu
 */
export const adminGetRevenueSummary = async (params = {}) => {
  try {
    const response = await api.get('/admin/summary', { params });
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

/**
 * Admin duyệt claim
 */
export const adminApproveRevenue = async (id) => {
  try {
    const response = await api.post(`/admin/${id}/approve`);
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

/**
 * Admin từ chối claim
 */
export const adminRejectRevenue = async (id, reason) => {
  try {
    const response = await api.post(`/admin/${id}/reject`, { reason });
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

/**
 * Admin duyệt / từ chối hàng loạt
 * @param {string[]} ids - danh sách revenue id
 * @param {'approve'|'reject'} action
 * @param {string} [reason] - bắt buộc khi action='reject'
 */
export const adminBatchRevenueAction = async (ids, action, reason) => {
  try {
    const response = await api.post('/admin/batch', { ids, action, reason });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

/**
 * Admin sửa doanh thu
 */
export const adminUpdateRevenue = async (id, data) => {
  try {
    const response = await api.put(`/admin/${id}`, data);
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

/**
 * Admin xóa doanh thu
 */
export const adminDeleteRevenue = async (id, reason) => {
  try {
    const response = await api.delete(`/admin/${id}`, { data: { reason } });
    return { success: true, message: response.data.message };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};

/**
 * Lấy audit log
 */
export const getRevenueAuditLog = async (id) => {
  try {
    const response = await api.get(`/audit/${id}`);
    return { success: true, data: response.data.data };
  } catch (error) {
    return { success: false, error: handleError(error) };
  }
};
